import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { secondaryButton } from "@tKgpco/Extensions/Widgets/MyLists/MyListsCreateListButtonExtension";
import * as cssLinter from "css";
import useEmblaCarousel from "embla-carousel-react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// declare var window: Window & typeof globalThis;

interface SlideModel {
    fields: {
        slideTitle: string;
        background: "image" | "color" | "video";
        image: string;
        mobileImage: string;
        imageOverlay: string;
        partialOverlay: boolean;
        partialOverlayPositioning: "top" | "middle" | "bottom";
        responsiveImageBehavior: "cover" | "center" | "prioritizeHeight" | "prioritizeWidth";
        backgroundColor: string;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        focalPoint:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        contentPadding: number;
        centerTextVertically: boolean;
        content: string;
        imageRedirectLink: string;
        imageRedirectNewTab: boolean;
        customCSS: string;
    };
}

const enum fields {
    height = "height",
    mobileHeight = "mobileHeight",
    textAlignment = "textAlignment",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    content = "content",
    imageRedirectLink = "imageRedirectLink",
    imageRedirectNewTab = "imageRedirectNewTab",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.height]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.mobileHeight]: "1/4 viewport" | "1/2 viewport" | "3/4 viewport" | "fullViewport";
        [fields.textAlignment]: "left" | "center" | "right";
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.slides]: SlideModel[];
        [fields.customCSS]: string;
        // [fields.content]: string;
    };
    extendedStyles?: SlideshowStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        buttonLinks: ownProps.fields.slides.map(slide => getLink(state, slide.fields.buttonLink)),
    };
};

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: InjectableCss;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    videoWrapper?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            position: relative;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            // top: auto;
                            // bottom: 8%;
                            display: flex;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    prevArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
            & {
                .prev-a {
                    fill: #151515;
                    opacity: 0.3;
                }
                .prev-b {
                    fill: none;
                    stroke: #fff;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-width: 2px;
                }
                svg {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 44px;
                                `,
                            ],
                            "max",
                        )}
                }
            }
        `,
    },
    iconProps: {
        size: 35,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            color: white;
            background-repeat: no-repeat;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideButton: {
        css: css`
            margin-top: 15px;
            & {
                span {
                    font-weight: bold;
                }
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -20px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${getColor("common.border")};
            &:hover,
            &:active {
                background: ${getColor("common.border")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            // top: auto;
                            // bottom: 8%;
                            display: flex;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    nextArrowButton: {
        css: css`
            background: none;
            border: 0;
            padding: 0;
            &:hover {
                background: none;
            }
            &:focus {
                outline: none;
            }
            & {
                .next-a {
                    fill: #151515;
                    opacity: 0.3;
                }
                .next-b {
                    fill: none;
                    stroke: #fff;
                    stroke-linecap: round;
                    stroke-linejoin: round;
                    stroke-width: 2px;
                }
                svg {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    width: 44px;
                                `,
                            ],
                            "max",
                        )}
                }
            }
        `,
    },
    headingText: {
        css: css`
            & p {
                margin-bottom: 10px !important;
                margin-right: 50%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                margin-right: 0;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    videoWrapper: {
        css: css`
            position: relative;
            /* padding-bottom: 56.25%;*/
            position: relative;
            padding-bottom: 28.25%;
            padding-top: 25px;
            height: 0;
            @media (max-width: 1199px) and (min-width: 992px) {
                padding-bottom: 30.25% !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding-bottom: 52.25%;
                        `,
                        css`
                            padding-bottom: 52.25%;
                        `,
                    ],
                    "max",
                )}
            iframe {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
            }
        `,
    },
    // slideCenteringWrapperStyles: {
    //     css: css`
    //         border:5px solid red;
    //     `,
    // }
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory;

const Slideshow = ({ fields, buttonLinks, history, extendedStyles }: Props) => {
    const { isInShell } = useShellContext();
    const [styles] = useState(() => mergeToNew(slideshowStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const imageLink = (fields: any) => {
        if (window && fields.background === "image") {
            if (fields.imageRedirectNewTab === true) {
                window.open(fields.imageRedirectLink, "_blank");
            } else {
                window.open(fields.imageRedirectLink, "_self");
            }
        }
    };

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    let heightStyles: string;
    switch (fields.height) {
        case "1/4 viewport":
            heightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            heightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            heightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            heightStyles = "min-height: 100vh;";
            break;
    }

    let mobileHeightStyles: string;
    switch (fields.mobileHeight) {
        case "1/4 viewport":
            mobileHeightStyles = "min-height: 25vh;";
            break;
        case "1/2 viewport":
            mobileHeightStyles = "min-height: 50vh;";
            break;
        case "3/4 viewport":
            mobileHeightStyles = "min-height: 75vh;";
            break;
        case "fullViewport":
            mobileHeightStyles = "min-height: 100vh;";
            break;
    }

    const textAlignStyles = `text-align: ${fields.textAlignment};`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            aria-label={translate("Previous")}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54">
                                <path
                                    className="prev-a"
                                    d="M0,0H27A27,27,0,0,1,54,27v0A27,27,0,0,1,27,54H0a0,0,0,0,1,0,0V0A0,0,0,0,1,0,0Z"
                                />
                                <g transform="translate(15.522 37.587) rotate(-90)">
                                    <path
                                        className="prev-b"
                                        d="M0,0,14.919,14.919"
                                        transform="translate(10.55 21.1) rotate(-135)"
                                    />
                                    <path
                                        className="prev-b"
                                        d="M8.173,0V8.173H0"
                                        transform="translate(10.549 11.558) rotate(-135)"
                                    />
                                </g>
                            </svg>
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    {/* <StyledWrapper>{parse(fields.content, parserOptions)}</StyledWrapper> */}
                    <StyledWrapper {...styles.slideContainerWrapper} className="slideshow-slide-container">
                        {fields.slides.map((slide, index) => {
                            const buttonLink = buttonLinks[index];

                            let responsiveImageBehaviorStyles;
                            if (slide.fields.responsiveImageBehavior === "cover") {
                                responsiveImageBehaviorStyles = "background-size: cover;";
                            } else if (slide.fields.responsiveImageBehavior === "center") {
                                responsiveImageBehaviorStyles = "background-size: contain;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeHeight") {
                                responsiveImageBehaviorStyles = "background-size: auto 100%;";
                            } else if (slide.fields.responsiveImageBehavior === "prioritizeWidth") {
                                responsiveImageBehaviorStyles = "background-size: 100% auto;";
                            }

                            const backgroundStyles =
                                slide.fields.background !== "image"
                                    ? `background-color: ${slide.fields.backgroundColor};
                                    background-image:none;`
                                    : ``;
                            const backgroundImage =
                                slide.fields.background === "image"
                                    ? `background-image: url(${parseImageUrl(slide.fields.image).url});`
                                    : ``;
                            const focalPointStyles = getFocalPointStyles(slide.fields.focalPoint);

                            let overlayPositioningStyles;
                            if (!slide.fields.partialOverlay) {
                                overlayPositioningStyles = "align-items: stretch;";
                            } else if (slide.fields.partialOverlayPositioning === "top") {
                                overlayPositioningStyles = "align-items: flex-start;";
                            } else if (slide.fields.partialOverlayPositioning === "middle") {
                                overlayPositioningStyles = "align-items: center;";
                            } else if (slide.fields.partialOverlayPositioning === "bottom") {
                                overlayPositioningStyles = "align-items: flex-end;";
                            }

                            let fontSizeStyles;
                            if (fields.responsiveFontSizes || fields.customFontSizes) {
                                fontSizeStyles = responsiveStyleRules(
                                    fields.responsiveFontSizes,
                                    fields.customFontSizes ? fields : undefined,
                                );
                            }

                            const mobileImage = slide.fields.mobileImage
                                ? slide.fields.mobileImage
                                : slide.fields.image;
                            const slideWrapperStyles = {
                                css: css`
                                    cursor: pointer;
                                    ${styles.slideContentWrapper?.css || ""}
                                    @media (max-width: 1199px) and (min-width: 992px) {
                                        min-height: 25vh !important;
                                    }
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    ${mobileHeightStyles}
                                                `,
                                                css`
                                                    ${mobileHeightStyles}
                                                `,
                                                css`
                                                    ${heightStyles}
                                                `,
                                            ],
                                            "min",
                                        )}
                                    ${textAlignStyles}    
                                ${backgroundImage}
                                ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                null,
                                                null,
                                                css`
                                                    background-image: url(${mobileImage});
                                                `,
                                            ],
                                            "max",
                                        )}
                                ${backgroundStyles}
                                ${responsiveImageBehaviorStyles}
                                ${focalPointStyles}
                                ${overlayPositioningStyles}
                                ${fontSizeStyles}
                                `,
                            };
                            const slideOverlayWrapperStyles = {
                                css: css`
                                    ${styles.slideOverlayWrapper?.css || ""}
                                    background-color: ${slide.fields.background === "image"
                                        ? slide.fields.imageOverlay
                                        : ""};
                                    padding: ${slide.fields.background === "video" ? 0 : slide.fields.contentPadding}px;
                                    ${({ theme }: { theme: BaseTheme }) =>
                                        breakpointMediaQueries(
                                            theme,
                                            [
                                                css`
                                                    padding: ${slide.fields.background === "video"
                                                        ? 0
                                                        : `0 40px 40px 40px`};
                                                `,
                                            ],
                                            "max",
                                        )}
                                    ${slide.fields.centerTextVertically &&
                                    `
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;`}
                                `,
                            };
                            return (
                                <>
                                    {/* <StyledWrapper key={index} {...slideWrapperStyles}> */}
                                    {/* <Link href="https://www.google.com/" > */}
                                    <StyledWrapper
                                        {...slideWrapperStyles}
                                        className="slide-wrapper"
                                        onClick={e => imageLink(slide.fields)}
                                    >
                                        <StyledWrapper {...slideOverlayWrapperStyles}>
                                            <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                                {slide.fields.heading && (
                                                    <Typography {...styles.headingText} className="slide-heading">
                                                        {parse(slide.fields.heading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {slide.fields.subheading && (
                                                    <Typography {...styles.subheadingText} className="slide-subheading">
                                                        {parse(slide.fields.subheading, parserOptions)}
                                                    </Typography>
                                                )}
                                                {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                    <StyledWrapper {...styles.slideButton}>
                                                        <Button
                                                            {...secondaryButton.createListButton}
                                                            // variant={slide.fields.buttonVariant}
                                                            onClick={() => onClick(buttonLink?.url)}
                                                            className="slide-button"
                                                        >
                                                            {slide.fields.buttonLabel ||
                                                                buttonLink?.title ||
                                                                buttonLink?.url}
                                                        </Button>
                                                    </StyledWrapper>
                                                )}
                                            </StyledWrapper>
                                            {slide.fields.content && (
                                                <StyledWrapper {...styles.videoWrapper}>
                                                    <iframe
                                                        width="100%"
                                                        height="100%"
                                                        src={slide.fields.content}
                                                        data-content-category="Ecommerce"
                                                        data-content-tracking="Homepage Video"
                                                        // src="https://www.youtube.com/embed/n_dZNLr2cME?rel=0&hd=1"
                                                        frameBorder="0"
                                                        allowFullScreen
                                                    ></iframe>
                                                </StyledWrapper>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    {/* </Link> */}
                                </>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.slideIndicator && (
                    <StyledWrapper {...styles.dotsContainerWrapper} className="slide-indicator">
                        {fields.slides.map((_, i) => {
                            const dotColorStyles =
                                i === selectedIndex
                                    ? `
                                    background: white;
                                    &:hover { background: white; }
                                `
                                    : "";
                            const dotButtonStyles = {
                                css: css`
                                    ${styles.dotButton?.css || ""}
                                    ${dotColorStyles}
                                `,
                            };
                            // eslint-disable-next-line react/no-array-index-key
                            return (
                                <Button
                                    className="slide-dot-button"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={i}
                                    {...dotButtonStyles}
                                    onClick={() => embla && embla.scrollTo(i)}
                                    aria-label={translate("Scroll to {0}", `${i + 1}`)}
                                />
                            );
                        })}
                    </StyledWrapper>
                )}
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.nextArrowWrapper} className="slide-arrow-wrapper">
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            className="slide-arrow"
                            aria-label={translate("Next")}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54">
                                <g transform="translate(0.221)">
                                    <path
                                        className="next-a"
                                        d="M27,0H54a0,0,0,0,1,0,0V54a0,0,0,0,1,0,0H27A27,27,0,0,1,0,27v0A27,27,0,0,1,27,0Z"
                                        transform="translate(-0.221 0)"
                                    />
                                    <g transform="translate(17.156 37.587) rotate(-90)">
                                        <path
                                            className="next-b"
                                            d="M0,14.919,14.919,0"
                                            transform="translate(21.099 10.55) rotate(135)"
                                        />
                                        <path
                                            className="next-b"
                                            d="M8.173,8.173V0H0"
                                            transform="translate(16.327 15.321) rotate(135)"
                                        />
                                    </g>
                                </g>
                            </svg>
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(Slideshow)),
    definition: {
        group: "Basic",
        icon: "Carousel",
        fieldDefinitions: [
            {
                name: fields.height,
                editorTemplate: "DropDownField",
                fieldType: "Translatable",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "3/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.mobileHeight,
                editorTemplate: "DropDownField",
                fieldType: "Translatable",
                options: [
                    { displayName: "1/4 Viewport", value: "1/4 viewport" },
                    { displayName: "1/2 Viewport", value: "1/2 viewport" },
                    { displayName: "3/4 Viewport", value: "3/4 viewport" },
                    { displayName: "Full Viewport", value: "fullViewport" },
                ],
                defaultValue: "1/4 viewport",
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.textAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "Translatable",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Translatable",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Translatable",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                getDisplay: (item: HasFields) => {
                    return item.fields.slideTitle;
                },
                defaultValue: [],
                fieldType: "Contextual",

                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: "background",
                        displayName: "Background",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Image", value: "image" },
                            { displayName: "Color", value: "color" },
                            { displayName: "Video", value: "video" },
                        ],
                        defaultValue: "image",
                        hideEmptyOption: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "mobileImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageRedirectLink",
                        displayName: "Image Link",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageRedirectNewTab",
                        displayName: "Open in new tab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "image",
                    },
                    {
                        name: "partialOverlay",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "partialOverlayPositioning",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top", value: "top" },
                            { displayName: "Middle", value: "middle" },
                            { displayName: "Bottom", value: "bottom" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "bottom",
                        // isVisible: widget => widget.fields.partialOverlay,
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "responsiveImageBehavior",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Cover", value: "cover" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Prioritize height", value: "prioritizeHeight" },
                            { displayName: "Prioritize width", value: "prioritizeWidth" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "cover",
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Color",
                        defaultValue: "white",
                        isVisible: widget => widget.fields.background === "color",
                    },
                    {
                        name: "focalPoint",
                        displayName: "Focal Point",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Top Left", value: "topLeft" },
                            { displayName: "Top Center", value: "topCenter" },
                            { displayName: "Top Right", value: "topRight" },
                            { displayName: "Center Left", value: "centerLeft" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Center Right", value: "centerRight" },
                            { displayName: "Bottom Left", value: "bottomLeft" },
                            { displayName: "Bottom Center", value: "bottomCenter" },
                            { displayName: "Bottom Right", value: "bottomRight" },
                        ],
                        defaultValue: "center",
                        hideEmptyOption: true,
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    // adapty custom code for video
                    // {
                    //     name: "content",
                    //     editorTemplate: "RichTextField",
                    //     displayName: "Video",
                    //     defaultValue: "",
                    // },
                    {
                        name: "content",
                        editorTemplate: "TextField",
                        // editorTemplate: "ImagePickerField",
                        displayName: "Video Url",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background === "video",
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    // {
                    //     name: "buttonVariant",
                    //     displayName: "Button Variant",
                    //     editorTemplate: "DropDownField",
                    //     options: [
                    //         { displayName: "Primary", value: "primary" },
                    //         { displayName: "Secondary", value: "secondary" },
                    //         { displayName: "Tertiary", value: "tertiary" },
                    //     ],
                    //     hideEmptyOption: true,
                    //     defaultValue: "primary",
                    // },
                    {
                        name: "contentPadding",
                        editorTemplate: "IntegerField",
                        defaultValue: 50,
                        isVisible: widget => widget.fields.background !== "video",
                    },
                    {
                        name: "centerTextVertically",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: widget => widget.fields.background !== "video",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });

                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
